import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, ArrowLeft, Target, ArrowDown, Heart, Storefront, Sparkle } from "@phosphor-icons/react";
import { SkeletonGrid } from "../components/Skeleton";
import FeedbackCTA from "../components/FeedbackCTA";

export default function Wishlist() {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ product_url: "", product_name: "", target_mode: "absolute", target_price: "", target_percent: "", notes: "" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/wishlist"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.product_url) { toast.error("Product URL is required"); return; }
    const usePercent = form.target_mode === "percent";
    if (usePercent) {
      const pct = parseFloat(form.target_percent);
      if (!pct || pct < 1 || pct > 90) { toast.error("Percent off must be between 1 and 90"); return; }
    } else if (!form.target_price) {
      toast.error("Target price is required"); return;
    }
    setAdding(true);
    // Harvey Norman can take ~15s to fetch because we have to defeat
    // Incapsula via Scrapfly. Surface a "still working" toast after 4s so
    // the user doesn't think the button froze.
    const slowTimer = setTimeout(() => {
      toast.info("Reading the product page — Harvey Norman takes ~15s, hold tight…", { id: "wishlist-slow", duration: 20000 });
    }, 4000);
    try {
      const payload = {
        product_url: form.product_url,
        product_name: form.product_name || form.product_url.split("/").pop(),
        notes: form.notes || null,
      };
      if (usePercent) {
        payload.target_percent = parseFloat(form.target_percent);
      } else {
        payload.target_price = parseFloat(form.target_price);
      }
      await api.post("/wishlist", payload);
      setForm({ product_url: "", product_name: "", target_mode: "absolute", target_price: "", target_percent: "", notes: "" });
      toast.dismiss("wishlist-slow");
      toast.success("Watching for price drops");
      load();
    } catch (err) {
      toast.dismiss("wishlist-slow");
      toast.error(err?.response?.data?.detail || "Could not add to wishlist");
    } finally {
      clearTimeout(slowTimer);
      setAdding(false);
    }
  };

  const refresh = async (id) => {
    try {
      await api.post(`/wishlist/${id}/refresh`);
      toast.success("Refreshed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Stop watching this product?")) return;
    try { await api.delete(`/wishlist/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <LaneNav active="wishlist" />

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="pg-overline mb-3">Buy-later watchlist</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Wishlist<span className="text-[#002FA7]">.</span></h1>
            <p className="text-[#52525b] mt-3 max-w-xl">Set a target price for products you haven't bought yet. We'll watch daily across PB Tech, JB Hi-Fi and Harvey Norman, and email you the moment the price hits your number.</p>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={submitNew} className="border border-[#e4e4e7] bg-[#f7f7f8] p-6 mb-12" data-testid="wishlist-add-form">
          <div className="pg-overline mb-4">Add a product to watch</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-6">
              <label className="pg-overline">Product URL</label>
              <input type="url" required value={form.product_url} onChange={(e)=>setForm(f=>({...f, product_url: e.target.value}))} className="mt-2 w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" placeholder="https://www.jbhifi.co.nz/products/…" data-testid="wishlist-url-input" />
            </div>
            <div className="md:col-span-3">
              <label className="pg-overline">Target</label>
              <div className="flex gap-1 mt-2 mb-2">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, target_mode: "absolute" }))}
                  className={`flex-1 text-[10px] font-bold py-1.5 px-2 border ${form.target_mode === "absolute" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#e4e4e7]"}`}
                  data-testid="wishlist-mode-absolute"
                >
                  $ AMOUNT
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, target_mode: "percent" }))}
                  className={`flex-1 text-[10px] font-bold py-1.5 px-2 border ${form.target_mode === "percent" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#e4e4e7]"}`}
                  data-testid="wishlist-mode-percent"
                >
                  % OFF
                </button>
              </div>
              {form.target_mode === "percent" ? (
                <input type="number" min="1" max="90" step="1" value={form.target_percent} onChange={(e)=>setForm(f=>({...f, target_percent: e.target.value}))} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" placeholder="15 (% off start)" data-testid="wishlist-target-percent-input" />
              ) : (
                <input type="number" min="0" step="0.01" value={form.target_price} onChange={(e)=>setForm(f=>({...f, target_price: e.target.value}))} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" placeholder="1500.00 NZD" data-testid="wishlist-target-input" />
              )}
            </div>
            <div className="md:col-span-3 flex items-end">
              <button type="submit" disabled={adding} className="w-full bg-[#002FA7] text-white font-bold py-3 hover:bg-[#00207A] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="wishlist-submit-btn">
                <Plus size={16} weight="bold" /> {adding ? "Adding…" : "Start watching"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
            <div className="md:col-span-6">
              <label className="pg-overline">Product name (optional)</label>
              <input type="text" value={form.product_name} onChange={(e)=>setForm(f=>({...f, product_name: e.target.value}))} className="mt-2 w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" placeholder="Auto-detected from URL if blank" data-testid="wishlist-name-input" />
            </div>
            <div className="md:col-span-6">
              <label className="pg-overline">Notes (optional)</label>
              <input type="text" value={form.notes} onChange={(e)=>setForm(f=>({...f, notes: e.target.value}))} className="mt-2 w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" placeholder="e.g. Buying for partner's birthday" data-testid="wishlist-notes-input" />
            </div>
          </div>
          <p className="text-xs text-[#52525b] mt-4 font-mono-pg flex items-center gap-2"><Storefront size={14} /> Supported: PB Tech · JB Hi-Fi · Harvey Norman</p>
          {/* Tracking-duration note. Free plan auto-stops daily checks
              after two weeks per item to keep the platform sustainable;
              Pro tracks indefinitely (handled in the daily scheduler by
              reading user.plan, not by storing a flag, so plan changes
              take effect on the next daily run). */}
          {isPro ? (
            <p className="text-xs text-[#16A34A] mt-2 font-mono-pg flex items-center gap-2" data-testid="wishlist-tracking-note-pro">
              <Sparkle size={14} weight="fill" /> Pro plan · we'll watch every wishlist item indefinitely, no expiry
            </p>
          ) : (
            <p className="text-xs text-[#D97706] mt-2 font-mono-pg flex items-start gap-2" data-testid="wishlist-tracking-note-free">
              <Sparkle size={14} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <span>Free plan · we watch each wishlist item daily for 2 weeks, then it stops. <Link to="/pricing" className="underline hover:text-black font-bold">Upgrade to Pro</Link> to watch indefinitely.</span>
            </p>
          )}
        </form>

        {/* List */}
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">Watching {items.length} product{items.length !== 1 && "s"}</h2>
        {loading ? (
          <div data-testid="wishlist-loading"><SkeletonGrid count={6} /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] py-16 px-6 text-center" data-testid="wishlist-empty">
            <Heart size={36} weight="duotone" color="#002FA7" className="mx-auto" />
            <h3 className="font-heading text-xl font-bold tracking-tight mt-3">No products on watch yet</h3>
            <p className="text-[#52525b] mt-2 max-w-md mx-auto">Paste a URL above and set a target price - we'll handle the rest.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((w) => {
              const hit = w.current_price <= w.target_price;
              const dropFromStart = w.starting_price - w.current_price;
              const pctFromTarget = ((w.current_price - w.target_price) / w.target_price) * 100;
              return (
                <div key={w.wish_id} className="pg-card p-5 pg-fade flex flex-col" data-testid={`wishlist-card-${w.wish_id}`}>
                  <Link to={`/dashboard/wishlist/${w.wish_id}`} className="flex gap-4 hover:opacity-80" data-testid={`wishlist-detail-link-${w.wish_id}`}>
                    <div className="w-16 h-16 bg-[#f0f0f2] flex-shrink-0 overflow-hidden border border-[#e4e4e7]">
                      {w.image_url && <img src={w.image_url} alt="" referrerPolicy="no-referrer" onError={(e)=>{ e.currentTarget.style.display="none"; }} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="pg-overline">{w.retailer_name}</div>
                      <span className="font-heading font-bold tracking-tight truncate block mt-1" title={w.product_name}>{w.product_name}</span>
                      {w.notes && <div className="font-mono-pg text-xs text-[#52525b] mt-1 truncate" title={w.notes}>{w.notes}</div>}
                    </div>
                  </Link>

                  <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[#e4e4e7] pt-4">
                    <div>
                      <div className="pg-overline">Target</div>
                      <div className="font-mono-pg font-semibold mt-1">${w.target_price.toFixed(2)}</div>
                      {w.target_percent ? (
                        <div className="font-mono-pg text-[10px] uppercase tracking-widest text-[#52525b] mt-0.5" data-testid={`wishlist-card-pct-${w.wish_id}`}>
                          {w.target_percent}% off start
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="pg-overline">Current</div>
                      <div className={`font-mono-pg font-semibold mt-1 ${hit ? "text-[#16A34A]" : ""}`}>${w.current_price.toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    {hit ? (
                      <span className="inline-flex items-center gap-1 bg-[#16A34A] text-white text-xs font-bold px-2 py-1" data-testid={`target-hit-${w.wish_id}`}>
                        <Target size={12} weight="bold" /> Target hit · save ${dropFromStart.toFixed(2)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border border-[#e4e4e7] text-xs font-semibold px-2 py-1 text-[#52525b]">
                        {pctFromTarget.toFixed(1)}% above target
                      </span>
                    )}
                    {/* Tracking countdown for Free users; Pro shows the
                        pro pill instead since they track indefinitely. */}
                    {w.tracking_expires_at ? (
                      <span className="text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b]" title="Free plan tracks each wishlist item for 14 days" data-testid={`wishlist-tracking-${w.wish_id}`}>
                        {(() => {
                          const left = Math.max(0, Math.ceil((new Date(w.tracking_expires_at) - new Date()) / 86400000));
                          return left > 0 ? `${left}d left` : "Expired";
                        })()}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono-pg uppercase tracking-widest text-[#16A34A]" title="Pro plan: tracked indefinitely" data-testid={`wishlist-tracking-${w.wish_id}`}>
                        Pro · forever
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex gap-2 pt-4 border-t border-[#e4e4e7]">
                    <button onClick={() => refresh(w.wish_id)} className="flex-1 border border-black text-xs font-bold py-2 hover:bg-black hover:text-white inline-flex items-center justify-center gap-1.5" data-testid={`refresh-${w.wish_id}`}>
                      <ArrowsClockwise size={12} /> Refresh
                    </button>
                    <button onClick={() => remove(w.wish_id)} className="border border-[#FF3B30] text-[#FF3B30] text-xs font-bold py-2 px-3 hover:bg-[#FF3B30] hover:text-white inline-flex items-center gap-1.5" data-testid={`remove-${w.wish_id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          <FeedbackCTA
            variant="strip"
            prompt="Want a retailer added or a price-watch feature?"
            subject="Wishlist feature idea"
          />
        </div>
      </main>
    </div>
  );
}
